import { HttpService } from 'services/HttpService';
import { API_URL, conf, tenantConfig } from 'config';
import AuthService from 'services/AuthService';
import { httpRequestWrapper } from 'util/Request';
import AuraAttribute, {
  AttributeUploadedFile,
  PreferenceQuestionAnswer,
  PrivatePreferencesResponseData,
} from 'store/types/AuraAttribute';
import CommunicationsPreferences, { CommunicationsPreferencesData } from 'store/types/CommunicationsPreferences';
import FileInfo from 'store/types/FileInfo';

type PreferenceQuestionsResponseData = AuraAttribute[];

class PreferencesService {
  public static getPreferenceQuestions(): Promise<PreferenceQuestionsResponseData> {
    return new Promise<PreferenceQuestionsResponseData>(
      (resolve: (data: PreferenceQuestionsResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<PreferenceQuestionsResponseData>(
          HttpService.get(`${API_URL}/preferences`, {
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getPreferenceQuestionsByAlternateId(alternateId: string): Promise<PrivatePreferencesResponseData> {
    return new Promise<PrivatePreferencesResponseData>(
      (resolve: (data: PrivatePreferencesResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<PrivatePreferencesResponseData>(
          HttpService.get(`${API_URL}/preferences/${alternateId}`, {
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getCommunicationsPreferences(id: string, isEncoded?: boolean): Promise<CommunicationsPreferences> {
    return new Promise<CommunicationsPreferences>(
      (resolve: (data: CommunicationsPreferences) => void, reject: (error: string) => void) => {
        httpRequestWrapper<CommunicationsPreferences>(
          HttpService.get(`${API_URL}/preferences/communications/${id}`, {
            queryParams: { isEncoded },
            headers: {
              TenantId: tenantConfig.id,
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getAttributeFile({ id, name }: FileInfo): Promise<null> {
    return new Promise<null>((resolve: (data: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper<null>(
        HttpService.getFileDownload(`${API_URL}/preferences/attachments/${id}`, {
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
            [conf.filenameHeader]: name,
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static uploadFile(
    file: File,
    questionId: string,
    sublistQuestionId?: string,
    referenceKey?: string
  ): Promise<AttributeUploadedFile> {
    const uploadedData = new FormData();

    uploadedData.append('file', file);
    return new Promise<AttributeUploadedFile>(
      (resolve: (data: AttributeUploadedFile) => void, reject: (error: string) => void) => {
        httpRequestWrapper<AttributeUploadedFile>(
          HttpService.post(`${API_URL}/preferences/attachments`, {
            data: uploadedData,
            queryParams: { questionId, sublistQuestionId, referenceKey },
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static updateCommunicationsPreferences(
    id: string,
    data: CommunicationsPreferencesData,
    isEncoded?: boolean
  ): Promise<null> {
    return new Promise<null>((resolve: (data: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper<null>(
        HttpService.put(`${API_URL}/preferences/communications/${id}`, {
          data,
          queryParams: { isEncoded },
          headers: {
            TenantId: tenantConfig.id,
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static updatePreferenceQuestions(data: PreferenceQuestionAnswer[]): Promise<PreferenceQuestionsResponseData> {
    return new Promise<PreferenceQuestionsResponseData>(
      (resolve: (value: PreferenceQuestionsResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper(
          HttpService.put(`${API_URL}/preferences`, {
            data,
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static updatePreferenceQuestionsByAlternateId(
    alternateId: string,
    data: PreferenceQuestionAnswer[]
  ): Promise<PreferenceQuestionsResponseData> {
    return new Promise<PreferenceQuestionsResponseData>(
      (resolve: (value: PreferenceQuestionsResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper(
          HttpService.put(`${API_URL}/preferences/${alternateId}`, {
            data,
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }
}
export default PreferencesService;
